import { Logger, withLogger } from '../../helpers/bi';
import {NewTenantParams, NewWixTenantParams, ErrorCodes, withTranslator, SignupType, isTenantCreation} from '../../helpers/shared';
import * as React from 'react';
import { StartSignup } from './start-signup/comp';
import { CompleteSignup } from './complete-signup/comp';
import { AccountState } from '../comp';
import { CaptchaParams, doSignupWithEmail, doSignupWithWix, doSignupRedirect } from '../../helpers/api.client';
import { BIEvent } from '../../helpers/bi/event-type';
import { MobileLock } from '../mobile-lock/comp';
import { checkIfMobile } from '../../helpers/utils';
import { gtmReportSignup, updateFreeTrialGtmEvent } from '../../helpers/gtm';
import { TranslateFn } from '@wix/answers-lib';
import { getMarketingParams } from '../../helpers/marketo/mkto-api-client';

export type SignupProps = Partial<NewTenantParams> & CaptchaParams;
export type SignupPropsWithWix = NewWixTenantParams & CaptchaParams;

export type CreateAccountProps = {
	onSignup: (subdomain: string, email: string, token: string | null) => void;
	isWix: boolean;
} & withLogger & withTranslator;

export class CreateAccount extends React.PureComponent<CreateAccountProps> {
	logger: Logger = this.props.logger;
	compTranslate: TranslateFn = (key, params) => this.props.t(`marketing-site.signup-form.signup.${key}`, params);
	requiredError = this.compTranslate('errors.required-field');

	captcha: any = null;
	subdomainCheckTimer: any = null;
	subdomainCheckDelay = 500;

	state = {
		signupStep: AccountState.SIGNUP,
		userEmail: '',
		userName: '',
		userPassword: '',
		organizationName: '',
		subdomain: '',
		locale: '',
		lockMobile: false,
		phoneNumber: undefined
	};

	onStartSignupSubmit = (params: Partial<NewTenantParams>) => this.setState({...params, signupStep: AccountState.SIGNUP_POST});

	onEmailSignupSubmit = (params: SignupProps) => {
		this.setState({...params}, () => this.signupWithEmail(params.recaptchaValue));
	}

	onWixSignupSubmit = (params: SignupPropsWithWix) => {
		doSignupWithWix(params)
			.then((res) => {
				const tenant = isTenantCreation(res) ? res.tenant : res;
				const token = isTenantCreation(res) ? res.token : null;

				this.logger.logSignupSuccess(BIEvent.SIGNUP_SUCCESS_STEP2, tenant, params, SignupType.WIX);
				gtmReportSignup(params.subdomain);

				if (checkIfMobile()) {
					this.setState({lockMobile: true});
				} else {
					this.props.onSignup(params.subdomain, params.email, token);
				}
			}, (err) => {
				if (err.data.errorCode === ErrorCodes.INVALID_WIX_TOKEN) {
					this.logger.logEvent(BIEvent.SIGNUP_FAILED_STEP2, {error: 'Invalid Wix token', params});
					alert(this.compTranslate('errors.wix-token-error-alert'));
				} else {
					this.logger.logEvent(BIEvent.SIGNUP_FAILED_STEP2, {error: `Server error - ${err.statusText}`, params});
					alert(this.compTranslate('errors.general-error-alert'));
				}

				doSignupRedirect();
			});
	}

	getUserData = (): NewTenantParams => {
		const {state} = this;

		const maybePhoneNumber = state.phoneNumber ? {phoneNumber: state.phoneNumber} : {};

		return {
			userEmail: state.userEmail,
			userPassword: state.userPassword,
			userName: state.userName,
			subdomain: state.subdomain,
			locale: state.locale,
			organizationName: state.organizationName,
			...maybePhoneNumber
		};
	}

	signupWithEmail = (recaptchaValue: string) => {
		const marketingParams = getMarketingParams();
		const params = {...this.getUserData(), recaptchaValue};

		doSignupWithEmail(params, marketingParams)
			.then((res) => {
				const tenant = isTenantCreation(res) ? res.tenant : res;
				const token = isTenantCreation(res) ? res.token : null;

				this.logger.logSignupSuccess(BIEvent.SIGNUP_SUCCESS_STEP2, tenant, params, SignupType.EMAIL);
				updateFreeTrialGtmEvent('organic');

				if (checkIfMobile()) {
					this.setState({lockMobile: true});
				} else {
					this.props.onSignup(params.subdomain, params.userEmail, token);
				}
			}, (err) => {
				this.logger.logEvent(BIEvent.SIGNUP_FAILED_STEP2, {error: `Server error - ${err.statusText}`, params});
				alert(this.compTranslate('errors.general-error-alert'));
				doSignupRedirect();
			});
	}

	render () {
		const {props} = this;

		if (this.state.lockMobile) {
			return <MobileLock t={props.t}/>;
		}

		if (this.props.isWix) {
			return <CompleteSignup logger={this.logger} onWixSubmit={this.onWixSignupSubmit} onSubmit={this.onEmailSignupSubmit} isWix={true} t={props.t}/>;
		}

		switch (this.state.signupStep) {
			default:
			case AccountState.SIGNUP:
				return <StartSignup logger={this.logger} onSubmit={this.onStartSignupSubmit} t={props.t}/>;

			case AccountState.SIGNUP_POST:
				return <CompleteSignup logger={this.logger} onWixSubmit={this.onWixSignupSubmit} onSubmit={this.onEmailSignupSubmit} t={props.t}/>;
		}
	}
}
